/**
 * 成绩分享工具
 *
 * 生成"一次考试成绩"的自包含分享链接：
 * 数据序列化为 JSON → UTF-8 → Base64URL，拼进 URL 路径。
 * 扫码用户无需登录、无需后端接口即可查看成绩单。
 *
 * 链接形式：<origin><pathname>#/share/score/<payload>
 */

export interface ShareScoreSubject {
  subject: string;
  score: number;
}

export interface ShareScorePayload {
  /** 数据版本，当前为 1 */
  v: number;
  studentName: string;
  grade?: string;
  className?: string;
  examName: string;
  examDate: string;
  stage?: string;
  subjects: ShareScoreSubject[];
  totalScore: number;
  /** 生成时间戳（毫秒），可选 */
  genTime?: number;
}

/** JSON → UTF-8 字节 → Base64URL（无填充） */
export function encodeScoreShare(data: ShareScorePayload): string {
  const json = JSON.stringify(data);
  const bytes = new TextEncoder().encode(json);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** Base64URL → 原始 JSON → ShareScorePayload，解析失败返回 null */
export function decodeScoreShare(payload: string): ShareScorePayload | null {
  try {
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const json = new TextDecoder().decode(bytes);
    const data = JSON.parse(json) as ShareScorePayload;
    if (
      !data ||
      typeof data !== "object" ||
      data.v !== 1 ||
      !data.studentName ||
      !data.examName ||
      !Array.isArray(data.subjects)
    ) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/** 构造完整分享链接（配合 hash 路由） */
export function buildScoreShareUrl(data: ShareScorePayload): string {
  const payload = encodeScoreShare(data);
  const { origin, pathname } = window.location;
  return `${origin}${pathname}#/share/score/${payload}`;
}
